import { useState } from 'react';
import { AutocompleteMenuSelectorProps } from './props';
import { AnimatePresence, motion } from 'framer-motion';
import { overlayVariant } from './variants';
import { FieldValues } from 'react-hook-form';
import SelectedOption from './SelectedOption';
import OptionsDrawer from './OptionsDrawer';
import styles from './styles.module.scss';

/**
 * A component that renders an autocomplete menu selector with a dropdown drawer of options
 *
 * @template T - Type extending FieldValues from react-hook-form
 * @template V - Type of the option value
 *
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of options to display in the dropdown
 * @param {Function} props.setValue - Function to set the selected value
 * @param {V} props.value - Currently selected value
 * @param {Function} props.filterBy - Function to filter options based on search input
 * @param {Function} props.getOptionLabel - Function to get the display label for an option
 * @param {Function} props.getOptionValue - Function to get the value for an option
 * @param {Object} props.customComponents - Custom components to override default rendering
 * @param {boolean} props.disabled - Whether the selector is disabled
 * @param {boolean} props.hasNextPage - Whether there are more options to load (for pagination)
 * @param {Function} props.loadMore - Function to load more options when scrolling
 * @param {boolean} props.loading - Whether options are currently loading
 * @param {boolean} props.loadingMore - Whether additional options are being loaded
 * @param {boolean} props.isAsync - Whether options are loaded asynchronously
 * @param {string} props.title - Title displayed in the options drawer
 * @param {boolean} props.isContainerColor - Whether to use container background color
 * @param {boolean} props.isSmallerDrawer - Whether to use a smaller drawer size
 * @param {boolean} props.hideSearch - Whether to hide the search input
 * @param {Function} props.onSearchChange - Callback when search input changes
 * @param {boolean} props.useGridOptions - Whether to display options in a grid layout
 *
 * @returns {JSX.Element} Autocomplete menu selector component with dropdown drawer
 */
export function AutocompleteMenuSelector<T extends FieldValues, V>({
  options,
  setValue,
  value,
  filterBy,
  getOptionLabel,
  getOptionValue,
  customComponents,
  disabled,
  hasNextPage = false,
  loadMore = () => undefined,
  loading = false,
  loadingMore = false,
  isAsync = false,
  title,
  isContainerColor,
  isSmallerDrawer,
  hideSearch,
  onSearchChange,
  useGridOptions,
}: AutocompleteMenuSelectorProps<T, V>) {
  // State to control the visibility of the options drawer
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      {/* Displays the currently selected option */}
      <SelectedOption
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        open={open}
        loading={loading}
        options={options}
        setOpen={setOpen}
        isContainerColor={isContainerColor}
        value={value}
        customComponents={customComponents}
        disabled={disabled}
      />

      <AnimatePresence>
        {open && !disabled && (
          <>
            {/* Animated overlay backdrop */}
            <motion.div
              className={styles.overlay}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={overlayVariant}
            />
            {/* Drawer containing the list of options */}
            <OptionsDrawer
              filterBy={filterBy}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
              options={options}
              setOpen={setOpen}
              setValue={setValue}
              title={title}
              useGridOptions={useGridOptions}
              value={value}
              customComponents={customComponents}
              hasNextPage={hasNextPage}
              isAsync={isAsync}
              loadMore={loadMore}
              loading={loading}
              loadingMore={loadingMore}
              isSmallerDrawer={isSmallerDrawer}
              hideSearch={hideSearch}
              onSearchChange={onSearchChange}
            />
          </>
        )}
      </AnimatePresence>
    </>
  );
}
